import clsx from 'clsx';
import { motion } from 'motion/react';
import { JSX, ReactNode, useMemo } from 'react';

import { Icon } from '../icon';
import { Button, ButtonProps } from '../button';

import { animateOpacity } from '../../constants/ui';

import './no-data.scss';

export const NoData = (props: NoDataProps) => {
  const {
    icon,
    query,
    title,
    actions,
    className,
    description,
    actionRenderer,
    size = 'default',
    color = 'default',
    as: Component = 'div',
    ...rest
  } = props;

  const MotionComponent = useMemo(() => motion.create(Component), []);

  const hasActions = !!actions?.length || typeof actionRenderer === 'function';
  const hasContent = !!title || !!description || hasActions;

  return (
    <MotionComponent {...animateOpacity} className={clsx('no-data-holder', `s-${size}`, `c-${color}`, className)} {...rest}>
      {icon}
      {hasContent && (
        <ul>
          {(!!title || !!description) && (
            <li>
              {!!title && <span>{title}</span>}
              {!!description && (
                <p>
                  {description}
                  {!!query && (
                    <span className="multiline" title={query}>
                      {query}
                    </span>
                  )}
                </p>
              )}
            </li>
          )}
          {typeof actionRenderer === 'function' && <li>{actionRenderer()}</li>}
          {!!actions?.length && (
            <li>
              {actions.map(({ icon, label, cornerRadius, ...buttonProps }, index) => (
                <Button
                  key={index}
                  cornerRadius={cornerRadius || 'rounded'}
                  icon={icon ? <Icon type={icon!} size={20} /> : undefined}
                  {...buttonProps}
                >
                  {label}
                </Button>
              ))}
            </li>
          )}
        </ul>
      )}
    </MotionComponent>
  );
};

interface Action extends Omit<ButtonProps, 'icon'> {
  icon?: string;
  label: string;
}

export interface NoDataProps {
  title?: string;
  query?: string;
  icon?: ReactNode;
  actions?: Action[];
  className?: string;
  description?: ReactNode;
  color?: 'darker' | 'default';
  actionRenderer?: () => ReactNode;
  as?: keyof JSX.IntrinsicElements;
  size?: 'default' | 'medium' | 'small';
}
